import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import axios from "axios";
import io from "socket.io-client";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import TrikotFormDialog from "../components/TrikotFormDialog";
import UserManagement from "../components/UserManagement";
import { API_BASE_URL, SOCKET_URL } from "../config";
import SortingFilter from "../components/SortingFilter";

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trikots, setTrikots] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTrikot, setSelectedTrikot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortOption, setSortOption] = useState("number");
  const [sortDirection, setSortDirection] = useState("asc");
  const [tabValue, setTabValue] = useState(0);
  const [isMigrating, setIsMigrating] = useState(false);
  const [finishedTrikots, setFinishedTrikots] = useState([]);
  const [loadingFinished, setLoadingFinished] = useState(false);

  useEffect(() => {
    fetchTrikots();
  }, []);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (!user.isAdmin) {
      navigate("/");
      return;
    }

    fetchTrikots();
  }, [user, navigate]);

  useEffect(() => {
    const socket = io(SOCKET_URL);

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
      setError("Verbindungsfehler zum Server");
    });

    fetchTrikots();

    socket.on("trikotUpdate", ({ trikotId, updatedTrikot }) => {
      setTrikots((prevTrikots) =>
        prevTrikots.map((trikot) =>
          trikot._id === trikotId ? { ...trikot, ...updatedTrikot } : trikot
        )
      );
    });

    socket.on("newTrikot", (newTrikot) => {
      setTrikots((prevTrikots) => [...prevTrikots, newTrikot]);
    });

    socket.on("deleteTrikot", (deletedTrikotId) => {
      setTrikots((prevTrikots) =>
        prevTrikots.filter((trikot) => trikot._id !== deletedTrikotId)
      );
    });

    socket.on("bidUpdate", ({ trikotId, currentBid, highestBidder }) => {
      setTrikots((prevTrikots) => {
        const newTrikots = prevTrikots.map((trikot) =>
          trikot._id === trikotId
            ? { ...trikot, currentBid, highestBidder }
            : trikot
        );

        if (sortOption === "currentBid") {
          return [...newTrikots].sort((a, b) => b.currentBid - a.currentBid);
        }
        return newTrikots;
      });
    });

    return () => {
      socket.disconnect();
    };
  }, [sortOption]);

  const fetchTrikots = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/trikots`);
      console.log("Geladene Trikots mit Geboten:", response.data.trikots.map(t => ({
        number: t.number,
        playerName: t.playerName,
        currentBid: t.currentBid,
        highestBidder: t.highestBidder,
        startingBid: t.startingBid
      })));
      setTrikots(response.data.trikots);
      setError(null);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(
        "Fehler beim Laden der Trikots: " +
          (err.response?.data?.message || "Verbindungsfehler zum Server")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setSelectedTrikot(null);
    setOpenDialog(true);
  };

  const handleEdit = (trikot) => {
    setSelectedTrikot(trikot);
    setOpenDialog(true);
  };

  const handleDeleteBid = async (trikotId) => {
    if (!window.confirm("Möchten Sie dieses Trikot wirklich löschen?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/api/trikots/${trikotId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setTrikots((prevTrikots) =>
        prevTrikots.filter((trikot) => trikot._id !== trikotId)
      );
      setSuccess("Trikot erfolgreich gelöscht");
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(
        "Fehler beim Löschen des Trikots: " +
          (error.response?.data?.message || error.message)
      );
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleMigrateImages = async () => {
    if (!window.confirm("Möchten Sie alle externen Bilder lokal speichern?")) {
      return;
    }

    try {
      setIsMigrating(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/api/trikots/migrate-images`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const results = response.data.results;
      setSuccess(
        `Bildmigration abgeschlossen:\n` +
          `Migriert: ${results.migrated}\n` +
          `Übersprungen: ${results.skipped}\n` +
          `Fehlgeschlagen: ${results.failed}`
      );

      // Refresh trikots to show updated image paths
      await fetchTrikots();
    } catch (error) {
      setError(
        "Fehler bei der Bildmigration: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setIsMigrating(false);
    }
  };

  const getSortedTrikots = () => {
    const sorted = [...trikots].sort((a, b) => {
      switch (sortOption) {
        case "number":
          return parseInt(a.number) - parseInt(b.number);
        case "currentBid":
          return b.currentBid - a.currentBid;
        case "endTime":
          return new Date(a.endTime) - new Date(b.endTime);
        default:
          return 0;
      }
    });
    return sortDirection === "desc" ? sorted.reverse() : sorted;
  };

  const handleSortChange = (newValue) => {
    setSortOption(newValue);
  };

  const handleDirectionChange = (newDirection) => {
    setSortDirection(newDirection);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
    if (newValue === 0) {
      fetchTrikots();
    } else if (newValue === 2) {
      fetchFinishedTrikots();
    }
  };

  const fetchFinishedTrikots = async () => {
    try {
      setLoadingFinished(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/trikots/finished`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFinishedTrikots(response.data.trikots);
      setError(null);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(
        "Fehler beim Laden der beendeten Auktionen: " +
          (err.response?.data?.message || "Verbindungsfehler zum Server")
      );
    } finally {
      setLoadingFinished(false);
    }
  };

  const handleExportCSV = () => {
    // CSV Header
    const headers = [
      "Spielernummer",
      "Spielername",
      "Höchstgebot",
      "Vorname",
      "Nachname",
      "Benutzername",
      "E-Mail",
    ];

    // CSV Daten
    const csvRows = [
      headers.join(","),
      ...finishedTrikots.map((trikot) => {
        return [
          trikot.number || "",
          trikot.playerName || "",
          trikot.currentBid || "0",
          trikot.highestBidderFirstName || "",
          trikot.highestBidderLastName || "",
          trikot.highestBidder || "",
          trikot.highestBidderEmail || "",
        ]
          .map((field) => {
            // Escape commas and quotes in CSV
            const stringField = String(field);
            if (stringField.includes(",") || stringField.includes('"')) {
              return `"${stringField.replace(/"/g, '""')}"`;
            }
            return stringField;
          })
          .join(",");
      }),
    ];

    // CSV String erstellen
    const csvContent = csvRows.join("\n");

    // BOM für UTF-8 (für Excel-Kompatibilität)
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    // Download-Link erstellen
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `beendete_auktionen_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSave = async (trikotData) => {
    try {
      const token = localStorage.getItem("token");
      let response;

      if (selectedTrikot) {
        response = await axios.put(
          `${API_BASE_URL}/api/trikots/${selectedTrikot._id}`,
          trikotData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTrikots((prevTrikots) =>
          prevTrikots.map((trikot) =>
            trikot._id === selectedTrikot._id ? response.data.trikot : trikot
          )
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/api/trikots`, trikotData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTrikots((prevTrikots) => [...prevTrikots, response.data.trikot]);
      }

      await fetchTrikots();
      setSuccess(
        selectedTrikot ? "Trikot aktualisiert" : "Neues Trikot erstellt"
      );
      setOpenDialog(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(
        "Fehler beim " +
          (selectedTrikot ? "Aktualisieren" : "Erstellen") +
          " des Trikots: " +
          (err.response?.data?.message || "Verbindungsfehler zum Server")
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Berechne die Gesamtsumme der aktuellen Gebote
  const totalCurrentBids = trikots.reduce((sum, trikot) => sum + trikot.currentBid, 0);

  if (loading) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
     
      <Box sx={{ width: "100%", mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} centered>
          <Tab label="Trikotverwaltung" />
          <Tab label="Benutzerverwaltung" />
          <Tab label="Beendete Auktionen" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <>
          <Box
            sx={{
              mb: 4,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Typography variant="h4" component="h2">
              Trikotverwaltung
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1, sm: 0 },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CloudDownloadIcon />}
                onClick={handleMigrateImages}
                disabled={isMigrating}
                sx={{ 
                  mr: { xs: 0, sm: 2 },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {isMigrating ? "Migration läuft..." : "Bilder lokal speichern"}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddNew}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Neues Trikot
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              sx={{ mb: 3 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}

          <SortingFilter
            sortOption={sortOption}
            onSortChange={handleSortChange}
            sortDirection={sortDirection}
            onDirectionChange={handleDirectionChange}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nummer</TableCell>
                  <TableCell>Spieler</TableCell>
                  <TableCell>Startgebot</TableCell>
                  <TableCell>Aktuelles Gebot</TableCell>
                  <TableCell>Höchstbietender</TableCell>
                  <TableCell>Enddatum</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trikots && trikots.length > 0 ? (
                  getSortedTrikots().map((trikot) =>
                    trikot._id ? (
                      <TableRow key={trikot._id}>
                        <TableCell>{trikot.number}</TableCell>
                        <TableCell>{trikot.playerName}</TableCell>
                        <TableCell>{trikot.startingBid}€</TableCell>
                        <TableCell>{trikot.currentBid}€</TableCell>
                        <TableCell>{trikot.highestBidder || '-'}</TableCell>
                        <TableCell>{formatDateTime(trikot.endTime)}</TableCell>
                        <TableCell>
                          {new Date(trikot.endTime) > new Date() ? (
                            <Typography color="primary">Aktiv</Typography>
                          ) : (
                            <Typography color="error">Beendet</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => handleEdit(trikot)}
                            color="primary"
                            title="Bearbeiten"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteBid(trikot._id)}
                            color="error"
                            title="Löschen"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ) : null
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Keine Trikots vorhanden
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={3} align="left"><strong>Gesamtsumme:</strong></TableCell>
                  <TableCell align="left"><strong>{totalCurrentBids.toFixed(2)}€</strong></TableCell>
                  <TableCell colSpan={4}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TrikotFormDialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            onSave={handleSave}
            trikot={selectedTrikot}
          />
        </>
      )}

      {tabValue === 1 && (
        <UserManagement onError={setError} onSuccess={setSuccess} />
      )}

      {tabValue === 2 && (
        <>
          <Box
            sx={{
              mb: 4,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Typography variant="h4" component="h2">
              Beendete Auktionen
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCSV}
              disabled={loadingFinished || finishedTrikots.length === 0}
              sx={{
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Als CSV exportieren
            </Button>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {loadingFinished ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Spielernummer</TableCell>
                    <TableCell>Spielername</TableCell>
                    <TableCell>Höchstgebot</TableCell>
                    <TableCell>Vorname</TableCell>
                    <TableCell>Nachname</TableCell>
                    <TableCell>Benutzername</TableCell>
                    <TableCell>E-Mail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {finishedTrikots && finishedTrikots.length > 0 ? (
                    finishedTrikots.map((trikot) =>
                      trikot._id ? (
                        <TableRow key={trikot._id}>
                          <TableCell>{trikot.number || "-"}</TableCell>
                          <TableCell>{trikot.playerName || "-"}</TableCell>
                          <TableCell>{trikot.currentBid?.toFixed(2) || "0.00"}€</TableCell>
                          <TableCell>
                            {trikot.highestBidderFirstName || "-"}
                          </TableCell>
                          <TableCell>
                            {trikot.highestBidderLastName || "-"}
                          </TableCell>
                          <TableCell>
                            {trikot.highestBidder || "-"}
                          </TableCell>
                          <TableCell>
                            {trikot.highestBidderEmail || "-"}
                          </TableCell>
                        </TableRow>
                      ) : null
                    )
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Keine beendeten Auktionen vorhanden
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Container>
  );
};

export default AdminDashboard;